import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import { formatRub, formatDate, plural } from '../lib/format.js'
import CoinIcon from '../components/CoinIcon.jsx'
import '../styles/deposit.css'

const METHOD_ICONS = {
  card: '💳',
  sbp: '⚡',
  yoomoney: '🥝',
  crypto: '₿',
}

const STATUS_LABELS = {
  pending: 'Ожидает оплаты',
  succeeded: 'Зачислено',
  canceled: 'Отменён',
}

const POLL_MS = 1200

export default function Deposit() {
  const { user, setBalance } = useAuth()
  const toast = useToast()

  const [meta, setMeta] = useState(null)
  const [method, setMethod] = useState('card')
  const [amount, setAmount] = useState('1000')
  const [creating, setCreating] = useState(false)
  const [pending, setPending] = useState(null)
  const [payments, setPayments] = useState(null)

  const [promo, setPromo] = useState('')
  const [promoBusy, setPromoBusy] = useState(false)

  const poll = useRef(null)
  useEffect(() => () => clearInterval(poll.current), [])

  useEffect(() => {
    api.paymentMethods().then(setMeta).catch((err) => toast.error(err.message))
    refreshHistory()
  }, [])

  function refreshHistory() {
    api.payments().then(({ payments }) => setPayments(payments)).catch(() => setPayments([]))
  }

  const amountKopecks = Math.round(Number(amount.replace(',', '.')) * 100) || 0
  const tooSmall = meta ? amountKopecks < meta.minKopecks : false
  const tooBig = meta ? amountKopecks > meta.maxKopecks : false
  const canCreate = amountKopecks > 0 && !tooSmall && !tooBig && !creating && !pending

  async function create() {
    if (!canCreate) return
    setCreating(true)
    try {
      const { payment } = await api.createPayment({ amount, method })
      setPending({ ...payment, status: 'pending' })
      startPolling(payment.id)
      refreshHistory()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setCreating(false)
    }
  }

  function startPolling(id) {
    clearInterval(poll.current)
    poll.current = setInterval(async () => {
      try {
        const { payment, balance } = await api.payment(id)
        setPending(payment)

        if (payment.status !== 'pending') {
          clearInterval(poll.current)
          if (balance !== null && balance !== undefined) setBalance(balance)
          if (payment.status === 'succeeded') {
            toast.success(`Баланс пополнен на ${formatRub(payment.amountKopecks)}`)
          }
          refreshHistory()
          setTimeout(() => setPending(null), 2600)
        }
      } catch (err) {
        clearInterval(poll.current)
        toast.error(err.message)
        setPending(null)
      }
    }, POLL_MS)
  }

  async function cancel() {
    if (!pending) return
    clearInterval(poll.current)
    try {
      await api.cancelPayment(pending.id)
    } catch {
      // Платёж мог успеть подтвердиться — просто перечитаем историю.
    }
    setPending(null)
    refreshHistory()
  }

  async function redeem(event) {
    event.preventDefault()
    if (!promo.trim() || promoBusy) return
    setPromoBusy(true)
    try {
      const data = await api.redeemPromo(promo.trim())
      setBalance(data.balance)
      toast.success(`Промокод активирован: +${formatRub(data.amountKopecks)}`)
      setPromo('')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setPromoBusy(false)
    }
  }

  return (
    <div className="page container deposit-page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Касса</span>
          <h1>Пополнение баланса</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>Все суммы — в</span>
            <CoinIcon size="xs" style={{ margin: '0 2px' }} />
            <span>. Баланс хранится без скрытых комиссий и списаний.</span>
          </p>
        </div>
        {user && (
          <div className="dep-balance card card-pad">
            <span className="tiny muted">Текущий баланс</span>
            <span className="dep-balance-value" style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span>{formatRub(user.balance)}</span>
              <CoinIcon size="md" />
            </span>
          </div>
        )}
      </div>

      {meta?.demo && (
        <div className="alert alert-info dep-demo">
          <strong>Демо-режим.</strong> Эквайринг не подключён: платёж подтверждается
          автоматически через пару секунд, настоящие деньги не списываются. Контур API
          повторяет реальный (создание → редирект → опрос статуса → зачисление), так что
          подключение провайдера сводится к замене одного модуля на сервере.
        </div>
      )}

      <div className="dep-layout">
        <section className="card card-pad">
          <h3 style={{ marginBottom: 16 }}>Способ оплаты</h3>

          <div className="method-grid">
            {(meta?.methods ?? []).map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`method-card ${method === entry.id ? 'is-active' : ''}`}
                onClick={() => setMethod(entry.id)}
                disabled={Boolean(pending)}
              >
                <span className="method-icon">{METHOD_ICONS[entry.id] ?? '💰'}</span>
                <span className="method-label">{entry.label}</span>
                <span className="tiny muted">{entry.hint}</span>
                <span className="tiny muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span>от</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>{entry.minRub}</span>
                    <CoinIcon size="xs" />
                  </span>
                </span>
              </button>
            ))}
            {!meta &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="skeleton" style={{ height: 128 }} />
              ))}
          </div>

          <hr className="divider" />

          <label className="field">
            <span className="field-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>Сумма пополнения</span>
              <CoinIcon size="xs" />
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(event) =>
                setAmount(event.target.value.replace(/[^\d.,]/g, '').slice(0, 9))
              }
              disabled={Boolean(pending)}
            />
          </label>

          <div className="quick-row">
            {(meta?.presetsKopecks ?? []).map((value) => (
              <button
                key={value}
                type="button"
                className={`chip ${amountKopecks === value ? 'is-active' : ''}`}
                onClick={() => setAmount(String(value / 100))}
                disabled={Boolean(pending)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
              >
                <span>{formatRub(value).replace(',00', '')}</span>
                <CoinIcon size="xs" />
              </button>
            ))}
          </div>

          {tooSmall && amountKopecks > 0 && (
            <div className="alert alert-error" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>Минимум</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <span>{formatRub(meta.minKopecks)}</span>
                <CoinIcon size="xs" />
              </span>
            </div>
          )}
          {tooBig && (
            <div className="alert alert-error" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>Максимум</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <span>{formatRub(meta.maxKopecks)}</span>
                <CoinIcon size="xs" />
              </span>
            </div>
          )}

          {pending ? (
            <div className={`pay-status is-${pending.status}`}>
              <div className="pay-status-head">
                <span className="strong">{STATUS_LABELS[pending.status]}</span>
                <span className="strong" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <span>{formatRub(pending.amountKopecks)}</span>
                  <CoinIcon size="sm" />
                </span>
              </div>
              {pending.status === 'pending' ? (
                <>
                  <div className="pay-bar">
                    <span />
                  </div>
                  <p className="tiny muted">
                    Платёж <span className="mono">{pending.id}</span> создан. Ждём
                    подтверждения от провайдера…
                  </p>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={cancel}>
                    Отменить
                  </button>
                </>
              ) : (
                <p className="tiny muted">
                  {pending.status === 'succeeded'
                    ? 'Деньги на балансе. Можно открывать кейсы.'
                    : 'Платёж отменён, средства не списаны.'}
                </p>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-lg btn-block"
              onClick={create}
              disabled={!canCreate}
            >
              {creating ? (
                'Создаём счёт…'
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span>Пополнить на</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>{formatRub(amountKopecks)}</span>
                    <CoinIcon size="sm" />
                  </span>
                </span>
              )}
            </button>
          )}
        </section>

        <aside className="dep-side">
          <div className="card card-pad">
            <h3 style={{ marginBottom: 6 }}>Промокод</h3>
            <p className="tiny muted" style={{ marginBottom: 14 }}>
              Один код — одна активация на аккаунт.
            </p>
            <form onSubmit={redeem}>
              <label className="field">
                <input
                  type="text"
                  value={promo}
                  onChange={(event) => setPromo(event.target.value.toUpperCase())}
                  placeholder="CASEMANIA"
                  autoComplete="off"
                />
              </label>
              <button
                type="submit"
                className="btn btn-soft btn-block"
                disabled={promoBusy || !promo.trim()}
              >
                {promoBusy ? 'Проверяем…' : 'Активировать'}
              </button>
            </form>
          </div>

          <div className="card dep-history">
            <div className="dep-history-head">
              <h3>История пополнений</h3>
              {payments && (
                <span className="tiny muted">
                  {payments.length}{' '}
                  {plural(payments.length, 'операция', 'операции', 'операций')}
                </span>
              )}
            </div>

            {payments === null ? (
              <div className="skeleton" style={{ height: 160, margin: 18 }} />
            ) : payments.length === 0 ? (
              <p className="muted tiny center" style={{ padding: 26 }}>
                Пополнений ещё не было.
              </p>
            ) : (
              <div className="dep-history-list">
                {payments.map((payment) => (
                  <div key={payment.id} className="dep-history-row">
                    <span className="method-icon small">
                      {METHOD_ICONS[payment.method] ?? '💰'}
                    </span>
                    <span className="dep-history-info">
                      <span className="tiny strong" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                        <span>{formatRub(payment.amountKopecks)}</span>
                        <CoinIcon size="xs" />
                      </span>
                      <span className="tiny muted">{formatDate(payment.createdAt)}</span>
                    </span>
                    <span className={`pill pay-${payment.status}`}>
                      {STATUS_LABELS[payment.status] ?? payment.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="tiny muted center">
            Играя, помни: это развлечение, а не источник дохода.
          </p>
        </aside>
      </div>
    </div>
  )
}
